import { Employee, Service, WorkHours } from '../types';
import { convertBusinessHoursToWorkHours } from '../utils/businessHours';
import { DAYJS_DAYS_OF_WEEK } from '../constants';
import { dayjs } from '../utils/dayjs-config';

// Preview booking: on a business's preview ("coming soon") site, the Book buttons
// open the real booking flow with ?preview=1 so a prospect can click through it.
// The flow uses the business's real services with one demo team member whose open
// times come from the opening hours. Nothing is ever saved: the confirm step stops
// with a preview message, and the server refuses bookings for businesses that
// aren't live regardless.

export const PREVIEW_EMPLOYEE_ID = 'preview-demo';
export const PREVIEW_DAYS = 60;

// Read once: the booking flow keeps the URL it was opened with.
const previewAtLoad = typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('preview') === '1';

export const isPreviewBooking = (): boolean => previewAtLoad;

export const previewEmployee = (singular = 'Team member'): Employee => ({
  id: PREVIEW_EMPLOYEE_ID,
  firstName: singular,
  lastName: '',
  admin: false,
  email: '',
  phone: '',
  appointments: {},
  active: true,
  bookable: true,
  isOrchestrated: false,
  isOwner: false,
  canAcceptCard: false,
} as Employee);

// Every service is offered by the demo team member.
export const withPreviewEmployee = (services: Service[]): Service[] =>
  services.map((service) => ({ ...service, employees: [PREVIEW_EMPLOYEE_ID] }));

// Open times for the next PREVIEW_DAYS days from the weekly opening hours, in the
// same MMDDYYYY → bitstring shape as a real schedule ('0' = open).
export const previewScheduleFromHours = (
  businessHours: Parameters<typeof convertBusinessHoursToWorkHours>[0],
  start = dayjs(),
): Map<string, string> => {
  const weekly: WorkHours = convertBusinessHoursToWorkHours(businessHours);
  const schedule = new Map<string, string>();
  for (let offset = 0; offset < PREVIEW_DAYS; offset += 1) {
    const day = start.add(offset, 'day');
    const weekday = DAYJS_DAYS_OF_WEEK[day.day()] as keyof WorkHours;
    const bits = weekly[weekday];
    if (bits) schedule.set(day.format('MMDDYYYY'), bits);
  }
  return schedule;
};
